import "./event-capture.scss";
import { useContext, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { BsSave } from "react-icons/bs";
import { useNavigate, useParams } from "react-router";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import Spinner from "../../../shared/components/spinner/spinner";
import ToolTip from "../../../shared/components/tooltip/tooltip";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { failedToast, getAllData, postSingleNonTable } from "../../../shared/utils/api";
import { usePrompt } from "../../../shared/utils/use-prompt";
import { createUUID, validateLoadingStatusNew } from "../../../shared/utils/utils";
import MultiTabComponent, { IMultiTabComponentConfig } from "../../components/multi-tab-component/multi-tab-component";
import Page from "../../components/page/page";
import ScreenSetup, { IScreenSetupData } from "./steps/screen-setup";
import ScreenConfiguration, { IScreenConfigurationData } from "./steps/screen-configuration";

const EventCapture = () => {
  const params = useParams();
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  const [setupScreenData, setSetupScreenData] = useState<IScreenSetupData>({name: "", event: "", event_id: "", alertcase_id: ""});
  const [screenConfigData, setScreenConfigData] = useState<IScreenConfigurationData>({layout: [], layoutItems: []});
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { flag, setFlag, next } = usePrompt(isDirty);

  const { sendRequest: postScreen, status: postScreenStatus, error: postScreenError } = useHttp(postSingleNonTable, false, authCtx.token, "capture",  "manager");
  const { sendRequest: getScreen, status: getScreenStatus, data: getScreenData, error: getScreenError } = useHttp(getAllData, false, authCtx.token, "capture", "manager");

  const pageIsNew = !params.id;

  useEffect(() => {
    if (!pageIsNew) {
      getScreen(`capture/${params.id}`);
    }
  }, []);

  useEffect(() => {
    if (getScreenData && !getScreenError) {
      setSetupScreenData({
        name: getScreenData.name,
        event: getScreenData.datamodel_name,
        event_id: getScreenData.datamodel_id,
        alertcase_id: getScreenData.alertcase_id
      });

      setScreenConfigData({
        layout: getScreenData.layout.layout,
        layoutItems: getScreenData.layout.layoutItems
      });
    }
  }, [getScreenStatus]);

  useEffect(() => {
    if (pageIsNew) {
      setScreenConfigData({layout: [], layoutItems: []});
    }
  }, [setupScreenData.event_id]);

  useEffect(() => {
    if (postScreenStatus === "completed" && !postScreenError) {
      navigate("/manager/event_capture_screens");
    }
  }, [postScreenStatus]);

  const handleSetupScreenDataChange = (data: IScreenSetupData) => {
    setIsDirty(true);
    setSetupScreenData(data);
  };

  const handleScreenSave = () => {
    const screenId = pageIsNew ? createUUID() : params.id;
    if (setupScreenData.name === "") {
      failedToast("Screen name is required");
      return;
    }
    if (setupScreenData.event_id === "") {
      failedToast("Event is required");
      return;
    }
    postScreen(`capture/${screenId}`, {
      id: screenId,
      name: setupScreenData.name,
      datamodel_id: setupScreenData.event_id,
      alertcase_id: setupScreenData.alertcase_id,
      layout: {
        layout: screenConfigData.layout,
        layoutItems: screenConfigData.layoutItems,
      },
      datamodel_type: "event"});
    setIsDirty(false);
  };

  const tabConfig: IMultiTabComponentConfig = {
    tabs: [{
      key: "screenSetup",
      name: "Screen Setup",
      content: <ScreenSetup screenData={setupScreenData} handleDataChange={handleSetupScreenDataChange}/>
    },{
      key: "layoutConfiguration",
      name: "Layout Configuration",
      content: <ScreenConfiguration 
        layout={{layout: screenConfigData.layout, layoutItems: screenConfigData.layoutItems}} 
        selectedEvent={setupScreenData.event_id} 
        handleDataChange={setScreenConfigData}/>,
      isVisible: () => setupScreenData.event !== ""
    }]
  };

  return <Page title={{
    text: setupScreenData.name,
    onTitleEdited: (title: string) => {
      setSetupScreenData({
        ...setupScreenData,
        name: title
      });
    },
    placeholder: "Event Capture Screen Name"
  }}
  breadCrumbItems={[
    {
      name: "Event Capture Screens",
      url: "/manager/event_capture_screens"
    },
    {
      name: "Event Capture Screen"
    }
  ]}
  >
    <div className="eventCapture">
      {validateLoadingStatusNew([postScreenStatus, getScreenStatus]) && <Spinner />}
      <div className="eventCapture-saveButton">
        <ToolTip content={"Save"} direction="top" delay={350}  >
          <Button disabled={postScreenStatus === "pending"} name="save" className="eventCapture-save" onClick={handleScreenSave}>
            <BsSave />
          </Button>
        </ToolTip>
      </div>
      <MultiTabComponent {...tabConfig}/>

      {flag && <ConfirmationPopup 
        show={flag}
        handleSaveClick={handleScreenSave}
        handlePopupClosed={() => {setFlag(false);}}
        handleContinue={() => { next(); setFlag(false); }} />}

    </div>
  </Page>;
};

export default EventCapture;